import { Slot } from "@radix-ui/react-slot";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { ChevronDown, Loader2, X } from "lucide-react";
import {
  forwardRef,
  useId,
  useLayoutEffect,
  useRef,
  type ButtonHTMLAttributes,
  type ChangeEvent,
  type KeyboardEvent,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { cn } from "../lib/cn";

/*
 * Base controls of the TURAQ design system. Heights: sm 36 · md 44 · lg 52 —
 * md and lg meet the 44 px touch target; sm is for dense desktop tables only.
 */

// ─── Button ──────────────────────────────────────────────────────────────

type ButtonVariant = "primary" | "brand" | "secondary" | "outline" | "ghost" | "danger";
type ButtonSize = "sm" | "md" | "lg";

const BUTTON_BASE =
  "p-focus relative inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-xl font-semibold transition-[background-color,box-shadow,transform,color] duration-150 ease-out active:scale-[.98] disabled:pointer-events-none disabled:opacity-55 [&_svg]:shrink-0";

const BUTTON_VARIANT: Record<ButtonVariant, string> = {
  primary: "bg-p-primary text-p-on-primary shadow-p-1 hover:bg-p-primary-hover",
  brand: "bg-p-gradient text-white shadow-p-2 hover:shadow-p-3 hover:-translate-y-px",
  secondary: "bg-p-primary-soft text-p-primary-ink hover:bg-p-primary-line/60",
  outline: "bg-p-surface text-p-ink ring-1 ring-inset ring-p-line-strong hover:bg-p-surface-2",
  ghost: "text-p-ink-2 hover:bg-p-surface-2 hover:text-p-ink",
  danger: "bg-p-danger-soft text-p-danger-ink hover:bg-p-danger/15",
};

const BUTTON_SIZE: Record<ButtonSize, string> = {
  sm: "h-9 px-3 text-[13px] [&_svg]:size-4",
  md: "h-11 px-4 text-[15px] [&_svg]:size-[18px]",
  lg: "h-[52px] px-6 text-base [&_svg]:size-5",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Render the child (a Link / <a>) with button styles. */
  asChild?: boolean;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", loading, asChild, block, className, children, disabled, type, ...rest },
  ref,
) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : (type ?? "button")}
      disabled={asChild ? undefined : disabled || loading}
      aria-busy={loading || undefined}
      className={cn(BUTTON_BASE, BUTTON_VARIANT[variant], BUTTON_SIZE[size], block && "w-full", className)}
      {...rest}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading && <Loader2 className="animate-spin" aria-hidden />}
          {children}
        </>
      )}
    </Comp>
  );
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  variant?: "ghost" | "outline" | "secondary" | "primary" | "glass";
  size?: "sm" | "md" | "lg";
}

const ICON_SIZE = { sm: "size-9 [&_svg]:size-4", md: "size-11 [&_svg]:size-5", lg: "size-12 [&_svg]:size-6" };
const ICON_VARIANT = {
  ghost: "text-p-ink-2 hover:bg-p-surface-2 hover:text-p-ink",
  outline: "bg-p-surface text-p-ink-2 ring-1 ring-inset ring-p-line hover:bg-p-surface-2",
  secondary: "bg-p-primary-soft text-p-primary-ink hover:bg-p-primary-line/60",
  primary: "bg-p-primary text-p-on-primary hover:bg-p-primary-hover",
  glass: "bg-black/35 text-white backdrop-blur-md hover:bg-black/50",
};

/** Square icon button; `label` becomes aria-label and the tooltip text. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, variant = "ghost", size = "md", className, children, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      aria-label={label}
      title={label}
      className={cn(
        "p-focus inline-flex shrink-0 items-center justify-center rounded-xl transition-colors duration-150 active:scale-95 disabled:opacity-50",
        ICON_SIZE[size],
        ICON_VARIANT[variant],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});

// ─── Form fields ─────────────────────────────────────────────────────────

export const FIELD =
  "w-full rounded-xl border border-p-line-strong bg-p-surface text-base text-p-ink placeholder:text-p-faint transition-[border-color,box-shadow] duration-150 hover:border-p-faint focus:border-p-primary focus:outline-none focus:shadow-[var(--p-ring)] disabled:cursor-not-allowed disabled:bg-p-surface-2 disabled:opacity-70 aria-invalid:border-p-danger";

interface FieldShellProps {
  id: string;
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

export function FieldShell({ id, label, hint, error, required, children, className }: FieldShellProps) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label && (
        <label htmlFor={id} className="text-[13px] font-semibold text-p-ink-2">
          {label}
          {required && <span className="ml-0.5 text-p-danger" aria-hidden>*</span>}
        </label>
      )}
      {children}
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-[13px] font-medium text-p-danger-ink">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-[13px] text-p-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  wrapperClassName?: string;
}

/*
 * A controlled number field fed `value={n}` with `Number(e.target.value)`
 * can't be emptied: clearing it gives 0, so the «0» stays and typing makes
 * «05»; «1.» is reported as "" and React wipes the dot. So the browser owns
 * the text of a number field (empty and «1.» are fine, leading zeros are
 * dropped) and every change is still reported; only a value changed from
 * outside (form reset, loaded data) rewrites the text.
 */
function useNumericField(
  { type, value, onChange, onKeyDown, min }: InputHTMLAttributes<HTMLInputElement>,
  forwarded: Ref<HTMLInputElement>,
) {
  const el = useRef<HTMLInputElement | null>(null);
  // `value={n ?? ""}` — an optional number that is empty right now — counts too.
  const active = type === "number" && (typeof value === "number" || value === "");
  useLayoutEffect(() => {
    const input = el.current;
    if (!active || !input || input.validity.badInput) return;
    const text = input.value.trim();
    const same = value === "" ? text === "" : text === "" ? value === 0 : Number(text) === value;
    if (!same) input.value = String(value);
  });
  const ref = (node: HTMLInputElement | null) => {
    el.current = node;
    if (typeof forwarded === "function") forwarded(node);
    else if (forwarded) forwarded.current = node;
  };
  if (!active) return { ref };
  // min ≥ 0 (floors, prices, days…): a minus or an exponent can't be typed or pasted.
  const unsigned = min !== undefined && Number(min) >= 0;
  return {
    ref,
    value: undefined,
    defaultValue: String(value),
    onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
      if (unsigned && ["-", "+", "e", "E"].includes(e.key)) e.preventDefault();
      onKeyDown?.(e);
    },
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.validity.badInput) return; // «1.» / «-» mid-typing
      let raw = e.target.value;
      if (unsigned && raw.startsWith("-")) e.target.value = raw = raw.slice(1);
      const text = raw.replace(/^(-?)0+(?=\d)/, "$1");
      if (text !== raw) e.target.value = text;
      onChange?.(e);
    },
  };
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, leading, trailing, id, className, wrapperClassName, required, ...rest },
  ref,
) {
  const auto = useId();
  const fieldId = id ?? auto;
  const numeric = useNumericField(rest, ref);
  return (
    <FieldShell id={fieldId} label={label} hint={hint} error={error} required={required} className={wrapperClassName}>
      <div className="relative">
        {leading && <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-p-faint [&_svg]:size-[18px]">{leading}</span>}
        <input
          id={fieldId}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
          className={cn(FIELD, "h-11", leading ? "pl-11" : "pl-3.5", trailing ? "pr-11" : "pr-3.5", className)}
          {...rest}
          {...numeric}
        />
        {trailing && <span className="absolute right-1.5 top-1/2 -translate-y-1/2">{trailing}</span>}
      </div>
    </FieldShell>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  wrapperClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, id, className, wrapperClassName, required, ...rest },
  ref,
) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <FieldShell id={fieldId} label={label} hint={hint} error={error} required={required} className={wrapperClassName}>
      <textarea
        ref={ref}
        id={fieldId}
        required={required}
        aria-invalid={error ? true : undefined}
        className={cn(FIELD, "min-h-28 px-3.5 py-3 leading-relaxed", className)}
        {...rest}
      />
    </FieldShell>
  );
});

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  options: { value: string; label: string; disabled?: boolean }[];
  placeholder?: string;
  wrapperClassName?: string;
}

/** Native select (the phone's own picker is the best mobile UX), styled. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, options, placeholder, id, className, wrapperClassName, required, ...rest },
  ref,
) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <FieldShell id={fieldId} label={label} hint={hint} error={error} required={required} className={wrapperClassName}>
      <div className="relative">
        <select
          ref={ref}
          id={fieldId}
          required={required}
          aria-invalid={error ? true : undefined}
          className={cn(FIELD, "h-11 cursor-pointer appearance-none pl-3.5 pr-10", className)}
          {...rest}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 size-[18px] -translate-y-1/2 text-p-faint" aria-hidden />
      </div>
    </FieldShell>
  );
});

// ─── Chip / Badge ────────────────────────────────────────────────────────

export interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onChange"> {
  selected?: boolean;
  onRemove?: () => void;
  icon?: ReactNode;
  count?: number;
}

/** Filter / toggle chip (aria-pressed). With onRemove it gets a ✕. */
export const Chip = forwardRef<HTMLButtonElement, ChipProps>(function Chip(
  { selected, onRemove, icon, count, className, children, type, ...rest },
  ref,
) {
  return (
    <span className="inline-flex">
      <button
        ref={ref}
        type={type ?? "button"}
        aria-pressed={selected}
        className={cn(
          "p-focus inline-flex h-10 items-center gap-1.5 rounded-full px-3.5 text-[14px] font-semibold transition-colors duration-150 active:scale-[.97] [&_svg]:size-4",
          selected
            ? "bg-p-ink text-p-surface"
            : "bg-p-surface text-p-ink-2 ring-1 ring-inset ring-p-line hover:bg-p-surface-2 hover:text-p-ink",
          onRemove && "pr-1.5",
          className,
        )}
        {...rest}
      >
        {icon}
        <span className="truncate">{children}</span>
        {count !== undefined && (
          <span className={cn("p-num rounded-full px-1.5 text-[12px]", selected ? "bg-white/20" : "bg-p-surface-2 text-p-muted")}>{count}</span>
        )}
        {onRemove && (
          <span
            role="button"
            tabIndex={-1}
            aria-hidden
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="ml-0.5 grid size-7 place-items-center rounded-full hover:bg-black/10"
          >
            <X className="size-3.5" />
          </span>
        )}
      </button>
    </span>
  );
});

type Tone = "neutral" | "primary" | "accent" | "success" | "warning" | "danger" | "info";

const BADGE_TONE: Record<Tone, string> = {
  neutral: "bg-p-surface-2 text-p-ink-2 ring-p-line",
  primary: "bg-p-primary-soft text-p-primary-ink ring-p-primary-line",
  accent: "bg-p-accent-soft text-p-accent ring-p-accent/20",
  success: "bg-p-success-soft text-p-success-ink ring-p-success/20",
  warning: "bg-p-warning-soft text-p-warning-ink ring-p-warning/25",
  danger: "bg-p-danger-soft text-p-danger-ink ring-p-danger/20",
  info: "bg-p-info-soft text-p-info-ink ring-p-info/20",
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: Tone;
  size?: "sm" | "md";
  icon?: ReactNode;
  dot?: boolean;
}

export function Badge({ tone = "neutral", size = "md", icon, dot, className, children, ...rest }: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1 rounded-full font-semibold ring-1 ring-inset [&_svg]:shrink-0",
        size === "sm" ? "h-6 px-2 text-[12px] [&_svg]:size-3" : "h-7 px-2.5 text-[13px] [&_svg]:size-3.5",
        BADGE_TONE[tone],
        className,
      )}
      {...rest}
    >
      {dot && <span className="size-1.5 rounded-full bg-current" aria-hidden />}
      {icon}
      <span className="truncate">{children}</span>
    </span>
  );
}

// ─── Card ────────────────────────────────────────────────────────────────

export interface CardProps extends HTMLAttributes<HTMLElement> {
  padding?: "none" | "sm" | "md" | "lg";
  interactive?: boolean;
  as?: "div" | "section" | "article" | "li";
}

const CARD_PAD = { none: "", sm: "p-3 sm:p-4", md: "p-4 sm:p-6", lg: "p-5 sm:p-8" };

export function Card({ padding = "md", interactive, as = "div", className, ...rest }: CardProps) {
  const Tag = as as "div";
  return (
    <Tag
      className={cn(
        "rounded-2xl border border-p-line bg-p-surface shadow-p-1",
        CARD_PAD[padding],
        interactive && "transition-[box-shadow,transform,border-color] duration-200 ease-out hover:-translate-y-0.5 hover:border-p-line-strong hover:shadow-p-2",
        className,
      )}
      {...rest}
    />
  );
}

export function SectionTitle({ title, action, subtitle, id }: { title: ReactNode; subtitle?: ReactNode; action?: ReactNode; id?: string }) {
  return (
    <div className="mb-4 flex items-end justify-between gap-3">
      <div className="min-w-0">
        <h2 id={id} className="text-[20px] font-extrabold tracking-tight text-p-ink sm:text-[24px]">
          {title}
        </h2>
        {subtitle && <p className="mt-1 text-[14px] text-p-muted">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

// ─── Skeleton ────────────────────────────────────────────────────────────

export function Skeleton({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div aria-hidden className={cn("p-shimmer rounded-xl", className)} {...rest} />;
}

export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn("flex flex-col gap-2", className)} aria-hidden>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className={cn("h-3.5", i === lines - 1 ? "w-3/5" : "w-full")} />
      ))}
    </div>
  );
}

// ─── Tabs ────────────────────────────────────────────────────────────────

export const Tabs = TabsPrimitive.Root;
export const TabsContent = TabsPrimitive.Content;

/** Underlined tabs; scroll sideways on phones instead of wrapping. */
export function TabsList({ items, className, sticky }: { items: { value: string; label: ReactNode; count?: number }[]; className?: string; sticky?: boolean }) {
  return (
    <div className={cn("border-b border-p-line", sticky && "p-glass sticky top-16 z-20", className)}>
      <TabsPrimitive.List className="-mb-px flex gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((it) => (
          <TabsPrimitive.Trigger
            key={it.value}
            value={it.value}
            className="p-focus group relative inline-flex h-12 shrink-0 items-center gap-2 rounded-t-lg px-3 text-[15px] font-semibold text-p-muted transition-colors hover:text-p-ink data-[state=active]:text-p-ink sm:px-4"
          >
            {it.label}
            {it.count !== undefined && (
              <span className="p-num rounded-full bg-p-surface-2 px-2 py-0.5 text-[12px] text-p-muted group-data-[state=active]:bg-p-primary-soft group-data-[state=active]:text-p-primary-ink">
                {it.count}
              </span>
            )}
            <span className="absolute inset-x-2 bottom-0 h-[3px] scale-x-0 rounded-full bg-p-primary transition-transform duration-200 ease-out group-data-[state=active]:scale-x-100" />
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>
    </div>
  );
}

// ─── SegmentedControl ────────────────────────────────────────────────────

export interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: ReactNode; icon?: ReactNode }[];
  label: string;
  size?: "sm" | "md";
  className?: string;
}

export function SegmentedControl<T extends string>({ value, onChange, options, label, size = "md", className }: SegmentedControlProps<T>) {
  return (
    <div role="radiogroup" aria-label={label} className={cn("inline-flex rounded-xl bg-p-surface-2 p-1 ring-1 ring-inset ring-p-line", className)}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            onKeyDown={(e) => {
              if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
              e.preventDefault();
              const i = options.findIndex((x) => x.value === value);
              const next = options[(i + (e.key === "ArrowRight" ? 1 : options.length - 1)) % options.length];
              onChange(next.value);
            }}
            tabIndex={active ? 0 : -1}
            className={cn(
              "p-focus inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg font-semibold transition-[background-color,color,box-shadow] duration-150 [&_svg]:size-4",
              size === "sm" ? "h-8 px-2.5 text-[13px]" : "h-9 px-3.5 text-[14px]",
              active ? "bg-p-surface text-p-ink shadow-p-1" : "text-p-muted hover:text-p-ink",
            )}
          >
            {o.icon}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Tooltip ─────────────────────────────────────────────────────────────

export function Tooltip({ content, children, side = "top" }: { content: ReactNode; children: ReactNode; side?: "top" | "bottom" | "left" | "right" }) {
  return (
    <TooltipPrimitive.Provider delayDuration={250}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={6}
            className="p-scale-in z-[220] max-w-64 rounded-lg bg-p-ink px-2.5 py-1.5 text-[12.5px] font-medium leading-snug text-p-surface shadow-p-2"
          >
            {content}
            <TooltipPrimitive.Arrow className="fill-p-ink" />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

// ─── Switch ──────────────────────────────────────────────────────────────

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "p-focus relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-200 disabled:opacity-50",
        checked ? "bg-p-primary" : "bg-p-line-strong",
      )}
    >
      <span className={cn("absolute left-0.5 size-6 rounded-full bg-white shadow-p-1 transition-transform duration-200 ease-out", checked && "translate-x-5")} />
    </button>
  );
}
